"use client";

import Link from "next/link";
import type { ReactNode } from "react";

import { cn } from "../../utils/cn.util";
import { Button } from "../button";
import { FoldedIcon, PharadayLogoIcon, UnFoldIcon, XIcon } from "../icons";
import { BasicTooltip } from "../tooltip";
import { useLayoutContext } from "./context";

export type LayoutSidebarHeaderProps = {
  className?: string;
  logoIcon?: ReactNode;
  name: string;
};

const SidebarHeader = ({ logoIcon, name, className }: LayoutSidebarHeaderProps) => {
  const { openSidebar, setOpenSidebar } = useLayoutContext();

  return (
    <>
      <div
        className={cn(
          "relative flex h-[57px] items-center justify-between border-b border-transparent py-3 pr-6 pl-[23px]",
          !openSidebar && "border-neutral-95",
          className
        )}
      >
        <Link href="/">
          <div
            className={cn(
              "flex cursor-pointer items-center justify-start gap-2.5",
              !openSidebar && "-translate-x-[5px] gap-[18px] transition-all",
              className
            )}
          >
            {logoIcon || <PharadayLogoIcon className="size-7" />}
            <span className={cn("text-base font-bold text-[#191C1E] transition-all", !openSidebar && "opacity-0")}>
              {name}
            </span>
          </div>
        </Link>

        <BasicTooltip
          className="absolute right-3 hidden items-center justify-center mobile-screen:flex"
          content={openSidebar ? "Hide sidebar" : "Show sidebar"}
          side="right"
          align="center"
        >
          <Button
            className={cn(
              "border-none transition-all duration-200 ease-in-out",
              !openSidebar &&
                "opacity-0 group-hover:opacity-100 focus:opacity-100 focus:shadow-button-secondary focus-visible:opacity-100",
              openSidebar ? "cursor-w-resize" : "cursor-e-resize"
            )}
            icon
            size="xs"
            variant="outline"
            color="secondary"
            onClick={() => setOpenSidebar?.(!openSidebar)}
          >
            {openSidebar ? (
              <UnFoldIcon className="size-5 rotate-180 text-neutral-50" />
            ) : (
              <FoldedIcon className="size-5 rotate-180 text-neutral-50" />
            )}
          </Button>
        </BasicTooltip>

        <Button
          icon
          size="xs"
          color="secondary"
          variant="ghost"
          onClick={() => {
            setOpenSidebar?.(false);
          }}
          className={cn("-mr-2 flex mobile-screen:hidden", className)}
        >
          <XIcon className="size-5 text-neutral-50" />
        </Button>
      </div>
    </>
  );
};

export default SidebarHeader;
